import '@tamagui/core/reset.css'
//import '@tamagui/font-inter/css/400.css'
//import '@tamagui/font-inter/css/700.css'
import { LoadingOverlay, OfflineBanner, PaymentIssueBanner } from '@my/ui'
import { ColorScheme, NextThemeProvider, useRootTheme } from '@tamagui/next-theme'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { queryClient } from 'app/data/api'
import { Provider } from 'app/providers'
import { AuthProviderProps } from 'app/providers/auth'
import { NextPage } from 'next'
import { DM_Sans } from 'next/font/google'
import Head from 'next/head'
import { useRouter } from 'next/router'
import 'raf/polyfill'
import { ReactElement, ReactNode, useEffect, useState } from 'react'
import type { SolitoAppProps } from 'solito'
import '../public/web.css'
if (process.env.NODE_ENV === 'production') {
  require('../public/tamagui.css')
}

const font = DM_Sans({
  variable: '--my-font',
  subsets: ['latin'],
})

export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode
}

function MyApp({
  Component,
  pageProps,
}: SolitoAppProps<{ initialSession: AuthProviderProps['initialSession'] }>) {
  // reference: https://nextjs.org/docs/pages/building-your-application/routing/pages-and-layouts
  const getLayout = Component.getLayout || ((page) => page)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_theme, setTheme] = useRootTheme()
  const [isRouteChanging, setIsRouteChanging] = useState(false)
  const [loadingTimeout, setLoadingTimeout] = useState<NodeJS.Timeout | null>(null)
  const router = useRouter()

  useEffect(() => {
    const handleStart = () => {
      // Clear any existing timeout
      if (loadingTimeout) clearTimeout(loadingTimeout)

      // Set a 150ms delay before showing the loading overlay
      const timeout = setTimeout(() => {
        setIsRouteChanging(true)
      }, 150)

      setLoadingTimeout(timeout)
    }

    const handleComplete = () => {
      if (loadingTimeout) clearTimeout(loadingTimeout)
      setIsRouteChanging(false)
    }

    router.events.on('routeChangeStart', handleStart)
    router.events.on('routeChangeComplete', handleComplete)
    router.events.on('routeChangeError', handleComplete)

    return () => {
      if (loadingTimeout) clearTimeout(loadingTimeout)
      router.events.off('routeChangeStart', handleStart)
      router.events.off('routeChangeComplete', handleComplete)
      router.events.off('routeChangeError', handleComplete)
    }
  }, [router, loadingTimeout])

  return (
    <>
      <Head>
        <title>Ari Login</title>
        <meta property="og:title" content="Ari Sign In"></meta>
        <meta
          name="description"
          content="Sign in to Ari, your personalized AI coach and assistant to help you get things done."
        />
        <meta
          property="og:description"
          content="Sign in to Ari, your personalized AI coach and assistant to help you get things done."
        />
        <meta property="og:image" content="https://app.helloari.ai/favicon.svg" />
        <meta
          name="viewport"
          content="width=device-width, user-scalable=no, initial-scale=1, maximum-scale=1, minimal-ui"
        />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-title" content="Ari" />
        <meta name="theme-color" content="#ecfdff" />
        <link rel="icon" href="/favicon.svg" />
      </Head>

      <QueryClientProvider client={queryClient}>
        <NextThemeProvider
          onChangeTheme={(next) => {
            setTheme(next as ColorScheme)
          }}
        >
          <Provider initialSession={pageProps.initialSession}>
            <div className={font.variable}>
              <OfflineBanner />
              <PaymentIssueBanner />
              {getLayout(<Component {...pageProps} />)}
            </div>
          </Provider>
        </NextThemeProvider>
        {/*   <ReactQueryDevtools initialIsOpen={false} />*/}
      </QueryClientProvider>
      {isRouteChanging && <LoadingOverlay />}
    </>
  )
}

export default MyApp
